"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { getSanitizedReferrer } from "@/components/utilities/sanitizeReferrer";
import { sanitizeReturnUrl } from "@/components/utilities/authentication/useRedirectToLogin";
import { useLogger } from "@/contexts/logger";
import { cleanBrokenPath } from "@/utils/cleanBrokenPath";

function NotFound() {
	const pathname = usePathname();
	const router = useRouter();
	const { captureMessage } = useLogger();

	// Keyed on the pathname, not run once: a repaired path that still 404s
	// renders this page again, and the second pass falls back to portfolio.
	useEffect(() => {
		// Repair copy and paste mistakes (e.g. a trailing non-breaking space)
		// first, so a link to /documents still lands on Documents. The repaired
		// path comes from the URL, so it gets the same same-origin guard as a
		// returnUrl before it reaches router.replace.
		const repaired = sanitizeReturnUrl(cleanBrokenPath(pathname ?? ""));
		const redirectTo = repaired
			? `${repaired}${window.location.search}${window.location.hash}`
			: "/portfolio";

		// Upstream pages that redirect here (e.g. /invest/[slug] when the offer
		// is missing) capture their own event with the original pathname before
		// redirecting, and tag the redirect with `?ref=stale-offer`. Reading the
		// query string directly (rather than via useSearchParams) keeps us off
		// the search-params subscription.
		const params = new URLSearchParams(window.location.search);
		if (params.get("ref") !== "stale-offer") {
			captureMessage("Investor portal 404", {
				level: "warning",
				tags: {
					event_type: "not_found",
					pathname: pathname ?? "(unknown)",
					redirect_to: repaired ?? "/portfolio",
					referrer: getSanitizedReferrer(),
				},
			});
		}

		// eslint-disable-next-line react-doctor/nextjs-no-client-side-redirect -- The target uses window.location, and the 404 is logged before the redirect.
		router.replace(redirectTo);
	}, [pathname, router, captureMessage]);

	return null;
}

export default NotFound;
